import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { 
  Package, Video, Plus, Edit2, Trash2, Save, X, Image as ImageIcon,
  ChevronDown, ChevronUp, Eye, EyeOff, DollarSign, Users, ShoppingBag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Admin password - in production, use proper auth
const ADMIN_PASSWORD = 'pureshmacks2024';

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('products');

  const handleAdminLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      toast.success('Admin access granted');
    } else {
      toast.error('Invalid password');
    }
  };

  if (!authenticated) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center" data-testid="admin-login">
        <div className="bg-[#0A0A0A] border border-[#27272A] p-8 w-full max-w-md">
          <h1 className="heading-display text-2xl text-white mb-6 text-center">Admin Access</h1>
          <form onSubmit={handleAdminLogin}>
            <Input
              type="password"
              placeholder="Admin Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field mb-4"
              data-testid="admin-password-input"
            />
            <Button type="submit" className="btn-primary w-full" data-testid="admin-login-btn">
              Access Dashboard
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" data-testid="admin-dashboard">
      <div className="container-main py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="heading-display text-3xl text-white">Admin Dashboard</h1>
          <Button onClick={() => setAuthenticated(false)} className="btn-secondary">
            Logout
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex gap-4 mb-8 bg-transparent flex-wrap">
            <TabsTrigger
              value="products"
              className="font-barlow font-bold uppercase data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white bg-transparent border border-[#27272A] text-white/60 px-6 py-3"
            >
              <Package className="w-5 h-5 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger
              value="mystery-boxes"
              className="font-barlow font-bold uppercase data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white bg-transparent border border-[#27272A] text-white/60 px-6 py-3"
            >
              <ShoppingBag className="w-5 h-5 mr-2" />
              Mystery Boxes
            </TabsTrigger>
            <TabsTrigger
              value="hits-gallery"
              className="font-barlow font-bold uppercase data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white bg-transparent border border-[#27272A] text-white/60 px-6 py-3"
            >
              <Video className="w-5 h-5 mr-2" />
              Hits Gallery
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            <ProductsManager />
          </TabsContent>

          <TabsContent value="mystery-boxes">
            <MysteryBoxManager />
          </TabsContent>

          <TabsContent value="hits-gallery">
            <HitsGalleryManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// Products Manager Component
function ProductsManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      // Filter out mystery boxes - they have their own tab
      setProducts(response.data.filter(p => !p.is_mystery_box));
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (product) => {
    try {
      if (product.product_id) {
        await axios.put(`${API}/products/${product.product_id}`, product);
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, product);
        toast.success('Product created');
      }
      fetchProducts();
      setEditingProduct(null);
      setShowAddForm(false);
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white">Regular Products</h2>
        <Button onClick={() => setShowAddForm(true)} className="btn-primary flex items-center gap-2">
          <Plus className="w-5 h-5" /> Add Product
        </Button>
      </div>

      {showAddForm && (
        <ProductForm
          onSave={handleSave}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {editingProduct && (
        <ProductForm
          product={editingProduct}
          onSave={handleSave}
          onCancel={() => setEditingProduct(null)}
        />
      )}

      <div className="space-y-4">
        {products.map(product => (
          <ProductRow
            key={product.product_id}
            product={product}
            onEdit={() => setEditingProduct(product)}
            onDelete={() => handleDelete(product.product_id)}
          />
        ))}
      </div>
    </div>
  );
}

// Mystery Box Manager Component
function MysteryBoxManager() {
  const [mysteryBoxes, setMysteryBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBox, setEditingBox] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchMysteryBoxes();
  }, []);

  const fetchMysteryBoxes = async () => {
    try {
      const response = await axios.get(`${API}/mystery-boxes`);
      setMysteryBoxes(response.data);
    } catch (error) {
      toast.error('Failed to fetch mystery boxes');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (box) => {
    try {
      const boxData = { ...box, is_mystery_box: true, category: 'mystery_box' };
      if (box.product_id) {
        await axios.put(`${API}/products/${box.product_id}`, boxData);
        toast.success('Mystery box updated');
      } else {
        await axios.post(`${API}/products`, boxData);
        toast.success('Mystery box created');
      }
      fetchMysteryBoxes();
      setEditingBox(null);
      setShowAddForm(false);
    } catch (error) {
      toast.error('Failed to save mystery box');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white">Mystery Boxes</h2>
        <Button onClick={() => setShowAddForm(true)} className="btn-primary flex items-center gap-2">
          <Plus className="w-5 h-5" /> Add Mystery Box
        </Button>
      </div>

      {showAddForm && (
        <MysteryBoxForm
          onSave={handleSave}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {editingBox && (
        <MysteryBoxForm
          box={editingBox}
          onSave={handleSave}
          onCancel={() => setEditingBox(null)}
        />
      )}

      <div className="space-y-4">
        {mysteryBoxes.map(box => (
          <MysteryBoxRow
            key={box.product_id}
            box={box}
            onEdit={() => setEditingBox(box)}
          />
        ))}
      </div>
    </div>
  );
}

// Hits Gallery Manager Component
function HitsGalleryManager() {
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingHit, setEditingHit] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchHits();
  }, []);

  const fetchHits = async () => {
    try {
      const response = await axios.get(`${API}/hits-gallery`);
      setHits(response.data);
    } catch (error) {
      console.error('Failed to fetch hits');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (hit) => {
    try {
      if (hit.hit_id && !hit.hit_id.startsWith('placeholder')) {
        await axios.put(`${API}/hits-gallery/${hit.hit_id}`, hit);
        toast.success('Hit updated');
      } else {
        await axios.post(`${API}/hits-gallery`, hit);
        toast.success('Hit created');
      }
      fetchHits();
      setEditingHit(null);
      setShowAddForm(false);
    } catch (error) {
      toast.error('Failed to save hit');
    }
  };

  const handleDelete = async (hitId) => {
    if (!window.confirm('Are you sure you want to delete this hit?')) return;
    try {
      await axios.delete(`${API}/hits-gallery/${hitId}`);
      toast.success('Hit deleted');
      fetchHits();
    } catch (error) {
      toast.error('Failed to delete hit');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white">Hits Gallery</h2>
        <Button onClick={() => setShowAddForm(true)} className="btn-primary flex items-center gap-2">
          <Plus className="w-5 h-5" /> Add Hit
        </Button>
      </div>

      {showAddForm && (
        <HitForm
          onSave={handleSave}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {editingHit && (
        <HitForm
          hit={editingHit}
          onSave={handleSave}
          onCancel={() => setEditingHit(null)}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {hits.map(hit => (
          <HitCard
            key={hit.hit_id}
            hit={hit}
            onEdit={() => setEditingHit(hit)}
            onDelete={() => handleDelete(hit.hit_id)}
          />
        ))}
      </div>

      {hits.length === 0 && !loading && (
        <div className="text-center py-12 text-white/60">
          <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>No hits yet. Add your first unboxing video!</p>
        </div>
      )}
    </div>
  );
}

// Product Form Component
function ProductForm({ product, onSave, onCancel }) {
  const [formData, setFormData] = useState(product || {
    name: '',
    description: '',
    price: 0,
    original_price: null,
    category: 'jersey',
    sport: 'football',
    athlete: '',
    team: '',
    authentication: '',
    images: [''],
    featured: false,
    stock_quantity: 1,
    in_stock: true
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (index, value) => {
    const newImages = [...(formData.images || [''])];
    newImages[index] = value;
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const addImageField = () => {
    setFormData(prev => ({ ...prev, images: [...(prev.images || []), ''] }));
  };

  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] p-6 mb-6">
      <h3 className="font-bold text-white text-lg mb-4">
        {product ? 'Edit Product' : 'Add New Product'}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-caps text-white/50 mb-1 block">Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="input-field"
            placeholder="Product name"
          />
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Price *</label>
          <Input
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => handleChange('price', parseFloat(e.target.value))}
            className="input-field"
          />
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Category</label>
          <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
            <SelectTrigger className="input-field">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-[#27272A]">
              <SelectItem value="jersey" className="text-white">Jersey</SelectItem>
              <SelectItem value="helmet" className="text-white">Helmet</SelectItem>
              <SelectItem value="ball" className="text-white">Ball</SelectItem>
              <SelectItem value="photo" className="text-white">Photo</SelectItem>
              <SelectItem value="funko" className="text-white">Funko</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Sport</label>
          <Select value={formData.sport} onValueChange={(v) => handleChange('sport', v)}>
            <SelectTrigger className="input-field">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-[#27272A]">
              <SelectItem value="football" className="text-white">Football</SelectItem>
              <SelectItem value="basketball" className="text-white">Basketball</SelectItem>
              <SelectItem value="baseball" className="text-white">Baseball</SelectItem>
              <SelectItem value="soccer" className="text-white">Soccer</SelectItem>
              <SelectItem value="multi" className="text-white">Multi-Sport</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Athlete</label>
          <Input
            value={formData.athlete || ''}
            onChange={(e) => handleChange('athlete', e.target.value)}
            className="input-field"
            placeholder="Athlete name"
          />
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Team</label>
          <Input
            value={formData.team || ''}
            onChange={(e) => handleChange('team', e.target.value)}
            className="input-field"
            placeholder="Team name"
          />
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Authentication</label>
          <Input
            value={formData.authentication || ''}
            onChange={(e) => handleChange('authentication', e.target.value)}
            className="input-field"
            placeholder="JSA, Beckett, PSA, etc."
          />
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Stock Quantity</label>
          <Input
            type="number"
            value={formData.stock_quantity}
            onChange={(e) => handleChange('stock_quantity', parseInt(e.target.value))}
            className="input-field"
          />
        </div>

        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Description</label>
          <Textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className="input-field min-h-[100px]"
            placeholder="Product description"
          />
        </div>

        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Images (URLs)</label>
          {(formData.images || ['']).map((img, idx) => (
            <Input
              key={idx}
              value={img}
              onChange={(e) => handleImageChange(idx, e.target.value)}
              className="input-field mb-2"
              placeholder="Image URL"
            />
          ))}
          <Button type="button" onClick={addImageField} className="btn-secondary text-sm">
            + Add Image
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <label className="text-white/70">Featured</label>
          <Switch
            checked={formData.featured}
            onCheckedChange={(v) => handleChange('featured', v)}
          />
        </div>

        <div className="flex items-center gap-4">
          <label className="text-white/70">In Stock</label>
          <Switch
            checked={formData.in_stock !== false}
            onCheckedChange={(v) => handleChange('in_stock', v)}
          />
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <Button onClick={() => onSave(formData)} className="btn-primary flex items-center gap-2">
          <Save className="w-5 h-5" /> Save
        </Button>
        <Button onClick={onCancel} className="btn-secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
}

// Mystery Box Form Component
function MysteryBoxForm({ box, onSave, onCancel }) {
  const [formData, setFormData] = useState(box || {
    name: '',
    description: '',
    price: 0,
    sport: 'multi',
    images: [''],
    featured: true,
    stock_quantity: 150,
    in_stock: true,
    is_mystery_box: true,
    series_name: 'Series 1',
    series_number: 1,
    edition_total: 150,
    editions_remaining: 150,
    headline_hits: [''],
    possible_hits: [''],
    box_features: [''],
    authentication_partners: ['JSA', 'Beckett', 'PSA/DNA', 'Fanatics'],
    coming_soon: false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    const newArr = [...(formData[field] || [''])];
    newArr[index] = value;
    setFormData(prev => ({ ...prev, [field]: newArr }));
  };

  const addArrayField = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...(prev[field] || []), ''] }));
  };

  const removeArrayField = (field, index) => {
    const newArr = [...(formData[field] || [])];
    newArr.splice(index, 1);
    setFormData(prev => ({ ...prev, [field]: newArr }));
  };

  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] p-6 mb-6 max-h-[80vh] overflow-y-auto">
      <h3 className="font-bold text-white text-lg mb-4">
        {box ? 'Edit Mystery Box' : 'Add New Mystery Box'}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="input-field"
            placeholder="e.g., Pure Shmacks Multi Sport Premium Autographed Jerseys - Series 1"
          />
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Price *</label>
          <Input
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => handleChange('price', parseFloat(e.target.value))}
            className="input-field"
          />
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Sport</label>
          <Select value={formData.sport} onValueChange={(v) => handleChange('sport', v)}>
            <SelectTrigger className="input-field">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-[#27272A]">
              <SelectItem value="football" className="text-white">Football</SelectItem>
              <SelectItem value="basketball" className="text-white">Basketball</SelectItem>
              <SelectItem value="baseball" className="text-white">Baseball</SelectItem>
              <SelectItem value="soccer" className="text-white">Soccer</SelectItem>
              <SelectItem value="multi" className="text-white">Multi-Sport</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Series Name</label>
          <Input
            value={formData.series_name || ''}
            onChange={(e) => handleChange('series_name', e.target.value)}
            className="input-field"
            placeholder="Series 1"
          />
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Edition Total</label>
          <Input
            type="number"
            value={formData.edition_total || 150}
            onChange={(e) => handleChange('edition_total', parseInt(e.target.value))}
            className="input-field"
          />
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Editions Remaining</label>
          <Input
            type="number"
            value={formData.editions_remaining || 150}
            onChange={(e) => handleChange('editions_remaining', parseInt(e.target.value))}
            className="input-field"
          />
        </div>

        <div className="flex items-center gap-4">
          <label className="text-white/70">Coming Soon</label>
          <Switch
            checked={formData.coming_soon}
            onCheckedChange={(v) => handleChange('coming_soon', v)}
          />
        </div>

        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Description</label>
          <Textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className="input-field min-h-[100px]"
            placeholder="Box description..."
          />
        </div>

        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Image URL</label>
          <Input
            value={formData.images?.[0] || ''}
            onChange={(e) => handleChange('images', [e.target.value])}
            className="input-field"
            placeholder="Main product image URL"
          />
        </div>

        {/* Headline Hits */}
        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Headline Hits (Chase Items)</label>
          {(formData.headline_hits || ['']).map((hit, idx) => (
            <div key={idx} className="flex gap-2 mb-2">
              <Input
                value={hit}
                onChange={(e) => handleArrayChange('headline_hits', idx, e.target.value)}
                className="input-field flex-1"
                placeholder="e.g., Tom Brady - The GOAT Chase!"
              />
              <Button type="button" onClick={() => removeArrayField('headline_hits', idx)} className="p-2 text-red-500">
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button type="button" onClick={() => addArrayField('headline_hits')} className="btn-secondary text-sm">
            + Add Headline Hit
          </Button>
        </div>

        {/* Box Features */}
        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Box Features</label>
          {(formData.box_features || ['']).map((feature, idx) => (
            <div key={idx} className="flex gap-2 mb-2">
              <Input
                value={feature}
                onChange={(e) => handleArrayChange('box_features', idx, e.target.value)}
                className="input-field flex-1"
                placeholder="e.g., One Premium Autographed Jersey Per Box"
              />
              <Button type="button" onClick={() => removeArrayField('box_features', idx)} className="p-2 text-red-500">
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button type="button" onClick={() => addArrayField('box_features')} className="btn-secondary text-sm">
            + Add Feature
          </Button>
        </div>

        {/* Possible Hits */}
        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Possible Hits (One per line or comma-separated)</label>
          <Textarea
            value={(formData.possible_hits || []).join(', ')}
            onChange={(e) => handleChange('possible_hits', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            className="input-field min-h-[150px]"
            placeholder="Tom Brady, Patrick Mahomes, LeBron James, etc."
          />
        </div>

        {/* Authentication Partners */}
        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Authentication Partners</label>
          <Input
            value={(formData.authentication_partners || []).join(', ')}
            onChange={(e) => handleChange('authentication_partners', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            className="input-field"
            placeholder="JSA, Beckett, PSA/DNA, Fanatics"
          />
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <Button onClick={() => onSave(formData)} className="btn-primary flex items-center gap-2">
          <Save className="w-5 h-5" /> Save
        </Button>
        <Button onClick={onCancel} className="btn-secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
}

// Hit Form Component
function HitForm({ hit, onSave, onCancel }) {
  const [formData, setFormData] = useState(hit || {
    title: '',
    description: '',
    video_url: '',
    image_url: '',
    product_name: '',
    athlete_name: '',
    customer_name: '',
    featured: false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] p-6 mb-6">
      <h3 className="font-bold text-white text-lg mb-4">
        {hit ? 'Edit Hit' : 'Add New Hit'}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Title *</label>
          <Input
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className="input-field"
            placeholder="e.g., Tom Brady Jersey Pull!"
          />
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Athlete Name</label>
          <Input
            value={formData.athlete_name || ''}
            onChange={(e) => handleChange('athlete_name', e.target.value)}
            className="input-field"
            placeholder="Tom Brady"
          />
        </div>

        <div>
          <label className="label-caps text-white/50 mb-1 block">Product Name</label>
          <Input
            value={formData.product_name || ''}
            onChange={(e) => handleChange('product_name', e.target.value)}
            className="input-field"
            placeholder="Pure Shmacks Series 1"
          />
        </div>

        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Video URL</label>
          <Input
            value={formData.video_url || ''}
            onChange={(e) => handleChange('video_url', e.target.value)}
            className="input-field"
            placeholder="https://your-video-url.mp4"
          />
          <p className="text-white/40 text-xs mt-1">Direct video URL (MP4, WebM, etc.)</p>
        </div>

        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Image/Thumbnail URL</label>
          <Input
            value={formData.image_url || ''}
            onChange={(e) => handleChange('image_url', e.target.value)}
            className="input-field"
            placeholder="https://your-image-url.jpg"
          />
        </div>

        <div className="md:col-span-2">
          <label className="label-caps text-white/50 mb-1 block">Description</label>
          <Textarea
            value={formData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            className="input-field min-h-[80px]"
            placeholder="Description of the hit..."
          />
        </div>

        <div className="flex items-center gap-4">
          <label className="text-white/70">Featured</label>
          <Switch
            checked={formData.featured}
            onCheckedChange={(v) => handleChange('featured', v)}
          />
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <Button onClick={() => onSave(formData)} className="btn-primary flex items-center gap-2">
          <Save className="w-5 h-5" /> Save
        </Button>
        <Button onClick={onCancel} className="btn-secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
}

// Product Row Component
function ProductRow({ product, onEdit, onDelete }) {
  return (
    <div className="bg-[#111111] border border-[#27272A] p-4 flex items-center gap-4">
      <img
        src={product.images?.[0] || 'https://via.placeholder.com/80'}
        alt={product.name}
        className="w-16 h-16 object-cover"
      />
      <div className="flex-1 min-w-0">
        <h4 className="font-bold text-white truncate">{product.name}</h4>
        <div className="flex gap-2 text-sm text-white/50">
          <span>${product.price}</span>
          <span>•</span>
          <span>{product.category}</span>
          <span>•</span>
          <span>{product.sport}</span>
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={onEdit} className="p-2 bg-[#27272A] hover:bg-[#3f3f46]">
          <Edit2 className="w-4 h-4 text-white" />
        </Button>
        <Button onClick={onDelete} className="p-2 bg-red-900/50 hover:bg-red-900">
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}

// Mystery Box Row Component
function MysteryBoxRow({ box, onEdit }) {
  return (
    <div className="bg-[#111111] border border-[#27272A] p-4 flex items-center gap-4">
      <img
        src={box.images?.[0] || 'https://via.placeholder.com/80'}
        alt={box.name}
        className="w-16 h-16 object-cover"
      />
      <div className="flex-1 min-w-0">
        <h4 className="font-bold text-white truncate">{box.name}</h4>
        <div className="flex gap-2 text-sm text-white/50">
          <span>${box.price}</span>
          <span>•</span>
          <span>{box.series_name}</span>
          <span>•</span>
          <span>{box.editions_remaining}/{box.edition_total} remaining</span>
        </div>
      </div>
      {box.coming_soon && (
        <Badge className="bg-yellow-500/20 text-yellow-500">Coming Soon</Badge>
      )}
      <Button onClick={onEdit} className="p-2 bg-[#27272A] hover:bg-[#3f3f46]">
        <Edit2 className="w-4 h-4 text-white" />
      </Button>
    </div>
  );
}

// Hit Card Component for Admin
function HitCard({ hit, onEdit, onDelete }) {
  return (
    <div className="bg-[#111111] border border-[#27272A] overflow-hidden">
      <div className="aspect-video bg-[#0A0A0A]">
        <img
          src={hit.image_url || 'https://via.placeholder.com/300x200'}
          alt={hit.title}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-4">
        <h4 className="font-bold text-white truncate">{hit.title}</h4>
        {hit.athlete_name && <p className="text-[#C41E3A] text-sm">{hit.athlete_name}</p>}
        <div className="flex gap-2 mt-4">
          <Button onClick={onEdit} className="flex-1 p-2 bg-[#27272A] hover:bg-[#3f3f46] text-xs">
            <Edit2 className="w-4 h-4 text-white" />
          </Button>
          <Button onClick={onDelete} className="p-2 bg-red-900/50 hover:bg-red-900">
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      </div>
    </div>
  );
}
